/**
 * 메일 수신 그룹 — 메일 화면의 독립 뷰.
 *
 * 왜 표가 아니라 카드인가
 *   연락처 화면은 '사람 한 명 = 한 행' 이라 표가 맞다. 그룹은 한 줄에 담을 정보가 이름·설명·역할별
 *   인원 셋이고 그중 둘이 가변 길이 문장이라, 표에 넣으면 설명이 잘리거나 행 높이가 들쭉날쭉해진다.
 */
import React, { useEffect, useState } from "react";
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import DeleteRoundedIcon  from "@mui/icons-material/DeleteRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";

import ContactGroupEditDialog from "./ContactGroupEditDialog";
import { roleSummary, membersToValue } from "./contactGroupAddress";
import ActionButton from "../../../../component/common/ActionButton";
import LoadingDots  from "../../../../component/common/LoadingDots";
import { mailApi }  from "../../api/mailApi";
import { ds }       from "../../../../styles/designSystem";
import { notify }   from "../../../../util/toast";
import { confirm }  from "../../../../util/confirmDialog";

/**
 * 메일 수신 그룹 — 메일 화면의 독립 뷰({@code MailPage view="groups"}).
 *
 * <h3>왜 연락처 탭이 아닌가</h3>
 * <p>한때 연락처 화면의 네 번째 탭이었다. 그런데 앞의 세 탭이 세우는 규칙 — 검색창, 표, 페이지 이동,
 * 'N개씩 보기' — 이 그룹에는 <b>하나도 성립하지 않는다</b>. 같은 탭 줄에 두면 헤더·본문을 탭마다
 * 갈아 끼우는 조건이 쌓이고, 어느 탭에서 어떤 규칙이 도는지 읽을 수 없게 된다.
 *
 * <p>성격도 다르다. 연락처 세 탭이 <b>사람 목록</b>이라면 이건 <b>발송 묶음</b>이다 —
 * 누구를 받는이·참조·숨은참조 중 어느 칸에 넣을지를 저장한다. 주소록보다 서명·차단 목록 같은
 * '메일 도구' 에 훨씬 가깝고, 그래서 사이드바에서도 그쪽에 선다.
 *
 * @param onComposeMail 그룹으로 바로 메일 쓰기. 넘기면 카드에 버튼이 생긴다.
 *   MailWriteModal 의 initial 형태({ tos, ccs, bccs })로 넘긴다.
 */
export default function ContactGroupPanel({ onComposeMail }) {
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(false);
    const [editDlg, setEditDlg] = useState(null);   // null = 닫힘, {} = 신규, {...} = 수정
    const [busyId, setBusyId]   = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await mailApi.listContactGroups();
            const data = res?.data ?? res;
            setRows(Array.isArray(data) ? data : []);
        } catch {
            setRows([]);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleDelete = async (g) => {
        const ok = await confirm({
            title: `'${g.name}' 삭제`,
            message: `그룹을 삭제합니다. 그룹에 담긴 연락처 자체는 지워지지 않습니다.`,
            confirmLabel: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        setBusyId(g.id);
        try {
            await mailApi.deleteContactGroup(g.id);
            notify("그룹을 삭제했습니다", "success");
            await load();
        } catch (e) {
            notify(e?.response?.data?.message || "삭제 실패", "error");
        } finally { setBusyId(null); }
    };

    /**
     * 그룹으로 메일 쓰기 — 목록에는 멤버가 없으므로 상세를 한 번 더 부른다.
     * (목록마다 멤버를 다 실어 보내면 그룹 수 × 멤버 수만큼 매번 오가는데, 실제로 쓰는 건 누른 하나뿐이다.)
     */
    const handleCompose = async (g) => {
        if (!onComposeMail) return;
        setBusyId(g.id);
        try {
            const res = await mailApi.getContactGroup(g.id);
            const members = (res?.data ?? res)?.members || [];
            if (members.length === 0) {
                notify("이 그룹에는 수신자가 없습니다", "warning");
                return;
            }
            onComposeMail({
                tos:  membersToValue(members, "TO"),
                ccs:  membersToValue(members, "CC"),
                bccs: membersToValue(members, "BCC"),
                subject: "",
            });
        } catch (e) {
            notify(e?.response?.data?.message || "그룹을 불러오지 못했습니다", "error");
        } finally { setBusyId(null); }
    };

    // 독립 뷰라 패딩·스크롤을 스스로 갖는다 — MailPage 는 flex 컨테이너만 준다
    // (서명·차단 목록 패널과 같은 형태).
    return (
        <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column",
                   overflow: "auto", ...ds.hiddenScrollbar }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", flex: 1 }}>
                    메일 수신 그룹
                    <Typography component="span" sx={{ ml: 1, fontSize: 12, color: "#94a3b8" }}>
                        {rows.length}건
                    </Typography>
                </Typography>
                <Tooltip title="새로고침">
                    <IconButton size="small" onClick={load}>
                        <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
                <ActionButton variant="add" onClick={() => setEditDlg({})} label="새 그룹" />
            </Stack>

            <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 2 }}>
                자주 함께 보내는 사람들을 묶어 두면, 작성창에서 그룹 하나로 받는이·참조·숨은참조가 한 번에 채워집니다.
            </Typography>

            {loading ? (
                <Box sx={{ py: 6, textAlign: "center", color: "#94a3b8" }}><LoadingDots /></Box>
            ) : rows.length === 0 ? (
                <Paper variant="outlined" sx={{ borderColor: "#e2e8f0", py: 6, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                        아직 만든 그룹이 없습니다. '새 그룹' 으로 시작하세요.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={1.2}>
                    {rows.map(g => (
                        <Paper
                            key={g.id}
                            variant="outlined"
                            onClick={() => setEditDlg(g)}
                            sx={{
                                borderColor: "#e2e8f0", p: 2, cursor: "pointer",
                                display: "flex", alignItems: "center", gap: 1.5,
                                "&:hover": { borderColor: "#93c5fd", bgcolor: "#fafbfc" },
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }} noWrap>
                                    {g.name}
                                </Typography>
                                {/* 인원 요약이 설명보다 위에 온다 — 그룹을 고를 때 먼저 확인하는 건
                                    "몇 명에게 어떻게 나가는가" 이지 설명 문장이 아니다. */}
                                <Typography sx={{ fontSize: 12, color: "#2563eb", fontWeight: 600, mt: 0.3 }}>
                                    {roleSummary(g)}
                                </Typography>
                                {g.description && (
                                    <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.2 }} noWrap>
                                        {g.description}
                                    </Typography>
                                )}
                            </Box>

                            {/* 카드 클릭은 '수정' 이라, 아래 두 버튼은 클릭을 삼켜야 한다. */}
                            <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                                {onComposeMail && (
                                    <Tooltip title="이 그룹으로 메일 작성">
                                        <span>
                                            <IconButton size="small" disabled={busyId === g.id}
                                                        onClick={() => handleCompose(g)}>
                                                <MailOutlineRoundedIcon sx={{ fontSize: 18, color: "#2563eb" }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                <Tooltip title="삭제">
                                    <span>
                                        <IconButton size="small" disabled={busyId === g.id}
                                                    onClick={() => handleDelete(g)}>
                                            <DeleteRoundedIcon sx={{ fontSize: 18, color: "#dc2626" }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <ContactGroupEditDialog
                open={Boolean(editDlg)}
                initial={editDlg}
                onClose={() => setEditDlg(null)}
                onSaved={async () => { setEditDlg(null); await load(); }}
            />
        </Box>
    );
}
